import { Box, Image, Tooltip, useBoolean } from '@chakra-ui/react';
import { DEFAULT_FIGURE_ALT_TEXT, TAG_FIGURE_IMAGE_CLASSNAME } from 'constants/altTextTagWidgets';
import { CustomOnResize, CustomOnResizeEnd } from 'modules/Editor/components/BoundingBox';
import { CSSProperties, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { hyperLinkStyle } from 'widgets/IconWidget/IconWidget.helpers';
import { ReadOnlyRenderType } from 'widgets/Widget.types';
import {
  LinkWrapper,
  ReadOnlyWidgetBase,
  TransparentImg,
  WidgetBase,
  WidgetBaseProp,
  WidgetToolbar,
  useWidget,
} from 'widgets/sdk';

import {
  IFRAMELY_KEY,
  TikTokShortsDimension,
  getTikTokId,
  getVideoIdFromYoutubeVideoUrl,
  getYoutubeImageFromId,
} from './VideoEmbedWidget.helper';
import { VideoEmbedWidgetData } from './VideoEmbedWidget.types';
import { VideoEmbedWidgetToolbar } from './VideoEmbedWidgetToolbar';

const YTExport = ({ videoId, altText, style }: { videoId: string; altText: string; style: CSSProperties }) => {
  // use max resolution to avoid images to be squished
  const videoImg = getYoutubeImageFromId(videoId, true);
  // using clickPath to cut 2px of the image to fix a black pixel that google adds to the images
  // @see  https://cloudinary.com/guides/automatic-image-cropping/5-ways-to-crop-images-in-html-css
  return (
    <Image
      alt={altText}
      src={videoImg}
      style={{ ...style, clipPath: 'inset(2px 0px)', position: 'absolute' }}
      className={TAG_FIGURE_IMAGE_CLASSNAME}
    />
  );
};

type Resize = {
  width: number;
  height: number;
  isResizing: boolean;
};

export const VideoEmbedWidget = () => {
  const { url, updateWidget } = useWidget<VideoEmbedWidgetData>();
  const [resize, setResize] = useState<Resize>({ isResizing: false, width: 0, height: 0 });

  const onChange = (newUrl: string | null) => {
    if (!newUrl) return;

    return updateWidget({ url: newUrl });
  };

  const onResizeEnd = useCallback(({ event, onResizeEnd }: CustomOnResizeEnd) => {
    setResize({
      height: 0,
      width: 0,
      isResizing: false,
    });
    onResizeEnd(event);
  }, []);

  const customOnResize = useCallback(({ event, onResize }: CustomOnResize) => {
    onResize(event);
    const widthPx = event.target.clientWidth;
    const heightPx = event.target.clientHeight;

    setResize({
      height: heightPx,
      width: widthPx,
      isResizing: true,
    });
  }, []);

  return (
    <WidgetBase onResize={customOnResize} onResizeEnd={onResizeEnd}>
      <WidgetToolbar>
        <VideoEmbedWidgetToolbar isActive onChange={onChange} value={url} />
      </WidgetToolbar>
      <VideoRender resize={resize} />
    </WidgetBase>
  );
};

/**
 * Default style for Video Widget
 */
const defaultStyle: CSSProperties = {
  overflow: 'hidden',
  top: '50%',
  left: '50%',
  position: 'absolute',
};

const VideoRender = ({ resize }: { resize?: Resize }) => {
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.videoEmbedMenu' });
  const [play, setPlay] = useBoolean();
  const { url, isWidgetSelected, renderType, widthPx, heightPx, altText, isDecorative } =
    useWidget<VideoEmbedWidgetData>();
  const videoId = getVideoIdFromYoutubeVideoUrl(url);
  const isYT = url.includes('youtu');
  const isTikTok = url.includes('tiktok');
  const isExportRender = renderType === ReadOnlyRenderType.ExportStructureTreeList;
  const isPreviewRender = renderType === ReadOnlyRenderType.StructureTreeList;
  const isThumbnailReadOnly = renderType === ReadOnlyRenderType.ThumbnailReadOnly;

  let scale = 1;

  let iframeURL = `https://cdn.iframe.ly/api/iframe?url=${encodeURIComponent(url)}&api_key=${IFRAMELY_KEY}`;
  if (isYT) {
    iframeURL = `https://www.youtube.com/embed/${videoId}?rel=0`;
  }
  if (isTikTok) {
    iframeURL = `https://www.tiktok.com/embed/v2/${getTikTokId(url)}?lang=en-US&referrer=${url}&embedFrom=oembed`;
    // TikTok videos are not responsive, so this is a workaround use by canvas to make it responsive
    if (resize?.isResizing) {
      scale = Math.min(resize?.width / TikTokShortsDimension.width, resize?.height / TikTokShortsDimension.height);
    } else {
      scale = Math.min(widthPx / TikTokShortsDimension.width, heightPx / TikTokShortsDimension.height);
    }
  }

  // remove iFrame interactivity when the widget is not selected
  useEffect(() => {
    if (!isWidgetSelected) {
      setPlay.off();
    }
  }, [isWidgetSelected, setPlay]);

  if (isYT && isExportRender) {
    // Since the video is a link on the readonly mode, altText must be displayed even when isDocorative is true.
    return (
      <YTExport
        videoId={videoId}
        altText={isDecorative ? DEFAULT_FIGURE_ALT_TEXT : altText || DEFAULT_FIGURE_ALT_TEXT}
        style={{
          width: widthPx,
          height: heightPx,
        }}
      />
    );
  }

  return (
    <Tooltip
      hasArrow
      label={t('doubleClick')}
      closeOnMouseDown
      isDisabled={play || isPreviewRender || isExportRender || isThumbnailReadOnly}
    >
      <Box position='relative' height='100%' width='100%' onDoubleClick={setPlay.on}>
        <iframe
          title='Video'
          src={iframeURL}
          frameBorder='0'
          allowFullScreen
          scrolling='no'
          allow='accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share;'
          style={{
            ...defaultStyle,
            width: isTikTok ? TikTokShortsDimension.width : '100%',
            height: isTikTok ? TikTokShortsDimension.height : '100%',
            transform: `translate(-50%, -50%) scale(${scale}, ${scale})`,
            pointerEvents: (play || isPreviewRender) && !resize?.isResizing && !isThumbnailReadOnly ? 'auto' : 'none', // prevents the interacting with the video when the user is dragging
          }}
        />
      </Box>
    </Tooltip>
  );
};

export const ReadOnlyVideoEmbedWidget = ({ includeAltTextImg }: WidgetBaseProp) => {
  const { url, renderType } = useWidget<VideoEmbedWidgetData>();

  const isYT = url.includes('youtu');
  const isExportRender = renderType === ReadOnlyRenderType.ExportStructureTreeList;

  return (
    <ReadOnlyWidgetBase>
      <LinkWrapper hyperlink={isExportRender ? url : ''} style={hyperLinkStyle}>
        <VideoRender />
        {includeAltTextImg && !isYT && isExportRender && <TransparentImg />}
      </LinkWrapper>
    </ReadOnlyWidgetBase>
  );
};
